"use client";

import React, { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="204485" className="flex gap-4">
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-default font-medium hover:text-primary hover:underline underline-offset-8 transition-all px-4 py-2 rounded-md hover:bg-accent/30">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-lg font-default font-medium text-foreground hover:text-primary transition-colors block py-3 px-4 rounded-md hover:bg-accent/30" onClick={onNavigate}>
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md" : "bg-background")}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="#" className="flex items-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/kim_julio_logo.png" alt="Kim & Julio Home Renewals" className="h-10 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="2">
              <DesktopNavConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-3">
            {}
            <Button asChild variant="default" className="hidden sm:flex font-default font-bold shadow-lg bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="tel:+16105498900">
                <Phone className="w-4 h-4 mr-2 animate-pulse" />
                Call Now
              </Link>
            </Button>

            {}
            <Button asChild variant="default" size="icon" className="sm:hidden shadow-lg bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="tel:+16105498900">
                <Phone className="w-4 h-4 animate-pulse" />
                <span className="sr-only">Call Now</span>
              </Link>
            </Button>

            {}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden text-foreground">
                  <Menu className="w-6 h-6" />
                  <span className="sr-only">Open main menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background border-l border-border">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">
                  Main navigation links for Kim & Julio Home Renewals
                </SheetDescription>
                <div className="flex flex-col h-full pt-6">
                  <div className="mb-8">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1623242/img/kim_julio_logo.png" alt="Kim & Julio Home Renewals" className="h-10 w-auto" />
                  </div>
                  <MenuProvider menu_id="2">
                    <MobileNavConsumer onNavigate={() => setIsMobileMenuOpen(false)} />
                  </MenuProvider>
                  
                  <div className="mt-auto pb-8">
                    <Button asChild variant="default" className="w-full font-default font-bold shadow-lg py-6 text-lg bg-primary text-primary-foreground">
                      <Link to="tel:+16105498900">
                        <Phone className="w-5 h-5 mr-2 animate-pulse" />
                        +16105498900
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}